import React from 'react';
import styled, { keyframes } from 'styled-components/macro';
import { createSelector } from 'reselect';
import orderBy from 'lodash/orderBy';
import { connect } from 'react-redux';
import { createGetItemValues, itemIsOutOfStock } from '../../features/commerce/items';
import { Table, Th, Td } from '../../components/tables';
import { allFadoel } from '../../content/commerce/fadoelTypes';
import { sanitizePrice } from '../../lib/price';
import { sanitizeMeasurement, ScreenSizeDetector } from '../../lib/helpers';
import { Item } from '../../containers/commerce';
import TableRow from './TableRow';
import HeadlineRow from './HeadlineRow';
import { fadoelOrder as defaultOrder, itemReverseLookup } from '../../content/commerce/itemLookup';
import { categories } from '../../content/commerce/itemCategories';
import { BsCaretDownFill, BsCaretUpFill, FaCog } from '../../components/Icons';

const StyledTable = styled(Table)`
@media (max-width: 991px) {
  th:nth-child(1),
  td:nth-child(1) {
    width: 180px;
  }
}
@media (max-width: 767px) {
  th:nth-child(1),
  td:nth-child(1) {
    width: 140px;
  }
}
@media (max-width: 575px) {
  th:nth-child(1),
  td:nth-child(1) {
    width: auto;
  }
}
`;

const IconStyled = styled.div`
  display: inline-block;
  position: relative;
  top: 3px;
`;

const IconSpin = styled.div`
  & svg {
    fill: ${({ theme }) => theme.colors.colorGrey};
    font-size: 30px;
  }

  animation: ${keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
  `} 2s linear infinite;
`;

const SortHead = ({
  onChangeSortBy,
  sortType,
  sortBy: {
    key,
    direction,
  },
  children,
  ...props
}) => (
  <Th
    {...props}
    onClick={
      onChangeSortBy
        ? () => {
          const newDirection = key === sortType && direction === 'ASC' ? 'DESC' : 'ASC';
          onChangeSortBy({ key: sortType, direction: newDirection });
        }
        : undefined
      }
  >
    {children} {
      sortType === key && direction === 'ASC'
        ? (
          <IconStyled><BsCaretUpFill /></IconStyled>
        )
        : (
          <IconStyled><BsCaretDownFill /></IconStyled>
        )
    }
  </Th>
);

export const SortTable = ({
  isLoading,
  items,
  sortBy,
  onChangeSortBy,
}) => {
  const rememberCategory = {};
  return (
    <ScreenSizeDetector paddingTd={[[767, '10px 5px'], '5px']}>
      <StyledTable lines2>
        <thead>
          <tr>
            <SortHead onChangeSortBy={onChangeSortBy} sortType="name" sortBy={sortBy}>
              Navn
            </SortHead>
            <SortHead textRight onChangeSortBy={onChangeSortBy} sortType="price" sortBy={sortBy}>
              Pris
            </SortHead>
            <SortHead textRight onChangeSortBy={onChangeSortBy} sortType="liter" sortBy={sortBy}>
              Liter
            </SortHead>
            <ScreenSizeDetector hide={[[767, true], false]}>
              <SortHead onChangeSortBy={onChangeSortBy} sortType="fadoelsType" sortBy={sortBy}>
                Øltype
              </SortHead>
            </ScreenSizeDetector>
            <ScreenSizeDetector hide={[[767, true], false]}>
              <SortHead onChangeSortBy={onChangeSortBy} sortType="land" sortBy={sortBy}>
                Land
              </SortHead>
            </ScreenSizeDetector>
            <ScreenSizeDetector hide={[[991, true], false]}>
              <SortHead textRight onChangeSortBy={onChangeSortBy} sortType="fadKobling" sortBy={sortBy}>
                Kobling
              </SortHead>
            </ScreenSizeDetector>
            <ScreenSizeDetector hide={[[575, true], [991, false], true]} block={[[575, false], [991, true], false]}>
              <SortHead textRight onChangeSortBy={onChangeSortBy} sortType="fadKobling" sortBy={sortBy}>
                Kobl.
              </SortHead>
            </ScreenSizeDetector>
            <ScreenSizeDetector hide={[[575, true], false]}>
              <SortHead textRight onChangeSortBy={onChangeSortBy} sortType="alcoholPercentage" sortBy={sortBy}>
                Alko %
              </SortHead>
            </ScreenSizeDetector>
            <ScreenSizeDetector hide={[[575, true], false]}>
              <SortHead textRight onChangeSortBy={onChangeSortBy} sortType="pricePrLiter" sortBy={sortBy}>
                Literpris
              </SortHead>
            </ScreenSizeDetector>
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? (
              <tr>
                <Td colSpan={8} textCenter><IconSpin><FaCog /></IconSpin></Td>
              </tr>
            )
            : (
              items.map(
                (varenr) => {
                  const category = itemReverseLookup[varenr];
                  const actualCategory = categories[category];
                  // const renderCategory = Boolean(category && actualCategory && rememberCategory.category !== category && !sortBy.key);
                  const renderCategory = false;
                  if (renderCategory) {
                    rememberCategory.category = category;
                  }
                  return (
                    <React.Fragment>
                      {renderCategory && (
                        <HeadlineRow category={actualCategory} />
                      )}
                      <Item varenr={varenr} key={varenr} display={TableRow} />
                    </React.Fragment>
                  );
                },
              )
            )
          }
        </tbody>
      </StyledTable>
    </ScreenSizeDetector>
  );
};

class ConnectedTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      items: null,
      isLoading: true,
      sortBy: { key: '', direction: 'ASC' },
    };
    this.onChangeSortBy = this.onChangeSortBy.bind(this);
    setTimeout(
      () => {
        const { itemValues } = props;
        const itemValuesExist = itemValues.reduce(
          (acc, itemValue) => ({
            ...acc,
            [itemValue.varenr]: true,
          }),
          {},
        );
        const inAllFadoel = allFadoel.reduce(
          (acc, varenr) => ({
            ...acc,
            [varenr]: true,
          }),
          {},
        );
        const orderedItems = defaultOrder.reduce(
          (acc, varenr) => {
            if (itemValuesExist[varenr] && inAllFadoel[varenr]) {
              inAllFadoel[varenr] = false;
              return [
                ...acc,
                varenr,
              ];
            }
            return acc;
          },
          [],
        );
        const existingItems = [
          ...orderedItems,
          ...allFadoel.filter(varenr => inAllFadoel[varenr] && itemValuesExist[varenr]),
        ];
        this.setState({
          items: existingItems,
          isLoading: false,
        });
      },
      0,
    );
  }

  onChangeSortBy({ key, direction }) {
    this.setState({
      isLoading: true,
      sortBy: { key, direction },
    });
    setTimeout(
      () => {
        const {
          itemValues,
        } = this.props;
        const sortedItems = orderBy(itemValues, [key], [direction.toLowerCase()]).map(({ varenr }) => varenr);
        this.setState({
          isLoading: false,
          items: sortedItems,
        });
      },
      0,
    );
  }

  render() {
    const {
      items,
      isLoading,
      sortBy,
    } = this.state;
    return (
      <SortTable
        isLoading={isLoading}
        items={items}
        sortBy={sortBy}
        onChangeSortBy={isLoading ? undefined : this.onChangeSortBy}
      />
    );
  }
}

export default connect(
  () => {
    const valuesSelector = createGetItemValues();
    const sortValuesSelector = createSelector(
      state => valuesSelector(state, {
        items: allFadoel,
        values: [
          'name',
          'price',
          'liter',
          'fadKobling',
          'alcoholPercentage',
          'land',
          'fadoelsType',
        ],
      }),
      state => state,
      (itemValues, state) => itemValues.filter(
        ({ varenr }) => !itemIsOutOfStock(state, varenr),
      ).map(
        ({
          varenr,
          name,
          price,
          liter,
          fadKobling,
          alcoholPercentage,
          land,
          fadoelsType,
        }) => {
          const actualPrice = sanitizePrice(price);
          const actualLiter = sanitizeMeasurement(liter);
          const pricePrLiter = actualPrice / actualLiter;
          return {
            varenr,
            name,
            price: actualPrice,
            liter: actualLiter,
            fadKobling: fadKobling && fadKobling.substring(0, 1),
            alcoholPercentage: sanitizeMeasurement(alcoholPercentage),
            land,
            fadoelsType,
            pricePrLiter,
          };
        },
      ),
    );
    return state => ({
      itemValues: sortValuesSelector(state),
    });
  },
)(ConnectedTable);
